import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Chip, 
  Skeleton,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemText
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AI_API_URL } from '../../constants';
import { getLanguageByCode } from '../../constants';
import { usePopularVideos } from '../../hooks/usePage';
import { usePeriodFilter } from '../../hooks/useHistory';
import PopularVideoCard from './PopularVideoCard';
import { truncateTitle } from '../../utils';

const PopularVideos = ({ isLoggedIn, userId, setLoginDialogOpen, checkAndExecute }) => {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // 분리된 커스텀 훅들 사용
  const periodFilter = usePeriodFilter();
  const { videos, loading, error, fetchPopularVideos } = usePopularVideos(
    isLoggedIn, 
    userId, 
    periodFilter.selectedPeriod
  );

  // 기간 선택 시 데이터 새로고침
  const handlePeriodChange = (value) => {
    fetchPopularVideos(value);
  };

  // 영상이 없는 경우에만 렌더링하지 않음 (로그인 상태와 무관하게 표시)
  if (!loading && videos.length === 0) {
    return null;
  }

  // 영상 클릭 처리
  const handleVideoClick = async (videoId) => {
    // 로그인하지 않은 경우 로그인 모달 표시
    if (!isLoggedIn || !userId) {
      if (setLoginDialogOpen) {
        setLoginDialogOpen(true);
      }
      return;
    }

    // 기능 차단 체크 (유예 기간 만료 시 모달 표시)
    if (checkAndExecute) {
      const canExecute = checkAndExecute(() => performVideoClick(videoId));
      if (!canExecute) return;
    } else {
      await performVideoClick(videoId);
    }
  };

  // 실제 영상 클릭 로직
  const performVideoClick = async (videoId) => {

    try {
      // 🎯 클릭한 영상의 정보 찾기
      const clickedVideo = videos.find(v => v.videoId === videoId);
      if (!clickedVideo) {
        console.error('클릭한 영상 정보를 찾을 수 없습니다:', videoId);
        return;
      }

      // 🌍 해당 영상의 원본 언어로 복사 (사용자 현재 언어가 아닌!)
      const originalLanguage = clickedVideo.language || 'ko';
      console.log(`🎯 [인기 영상 클릭] 영상 언어: ${originalLanguage}, 현재 사용자 언어: ${i18n.language}`);

      // 1️⃣ 개인 히스토리에서 중복 체크 먼저
      try {
        console.log(`🔍 [개인 히스토리] ${videoId} (${originalLanguage}) 검색 중...`);
        
        const personalCheckResponse = await fetch(`${AI_API_URL}/api/streaming/check-personal-history/${videoId}?language=${originalLanguage}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'user-id': userId
          }
        });
        
        if (personalCheckResponse.ok) {
          const personalCheckResult = await personalCheckResponse.json();
          
          if (personalCheckResult.exists) {
            console.log(`✅ [개인 히스토리] 기존 히스토리 발견! ID: ${personalCheckResult.historyId}`);
            
            // 언어 설정 처리
            const currentLanguage = i18n.language;
            if (originalLanguage !== currentLanguage) {
              console.log(`🔄 [인기 영상 클릭] 언어 변경: ${currentLanguage} → ${originalLanguage}`);
              await i18n.changeLanguage(originalLanguage);
            }
            
            // 기존 히스토리로 이동
            navigate(`/analyze/${personalCheckResult.historyId}?existingHistory=true&videoTitle=${encodeURIComponent(personalCheckResult.title || personalCheckResult.customTitle || '')}`);
            return;
          } else {
            console.log(`❌ [개인 히스토리] 없음, 광역 히스토리 복사 진행`);
          }
        } else {
          const errorText = await personalCheckResponse.text();
          console.warn(`⚠️ [개인 히스토리] API 에러:`, personalCheckResponse.status, errorText);
          // 개인 체크 실패 시 광역 복사 진행
        }
      } catch (error) {
        console.warn(`⚠️ [개인 히스토리] 네트워크 에러:`, error.message);
        // 개인 체크 실패 시 광역 복사 진행
      }

      // 2️⃣ 광역 히스토리에서 개인 히스토리로 복사 (원본 언어로)
      const response = await fetch(`${AI_API_URL}/api/streaming/copy-from-global-history/${videoId}?language=${originalLanguage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // 🌍 광역 히스토리의 언어 정보 확인 및 자동 설정
        const globalHistoryLanguage = result.language || originalLanguage;
        const currentLanguage = i18n.language;
        
        console.log(`🌍 [인기 영상 클릭] 언어 확인: 현재=${currentLanguage}, 광역히스토리=${globalHistoryLanguage}`);
        
        // 언어가 다른 경우 광역 히스토리의 언어로 변경
        if (globalHistoryLanguage !== currentLanguage) {
          console.log(`🔄 [인기 영상 클릭] 언어 변경: ${currentLanguage} → ${globalHistoryLanguage}`);
          await i18n.changeLanguage(globalHistoryLanguage);
        }
        
        // 복사된 히스토리로 이동
        navigate(`/analyze/${result.historyId}?globalCopy=true&videoTitle=${encodeURIComponent(result.title || '')}`);
      } else {
        console.error('광역 히스토리 복사 실패:', response.status);
      }
    } catch (err) {
      console.error('광역 히스토리 복사 에러:', err);
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '800px' },
        mx: 'auto',
        mt: 4, 
        mb: 2,
        px: { xs: 2, sm: 3, md: 2 }
      }}
    >
      {/* 제목과 필터 메뉴 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'bold',
            color: '#333',
            textAlign: 'left'
          }}
        >
          {t('landing.popularVideos')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              fontSize: '0.875rem'
            }}
          >
            {periodFilter.getCurrentPeriodLabel()}
          </Typography>
          <IconButton
            onClick={periodFilter.handleMenuOpen}
            size="small"
            sx={{
              color: '#666',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* 기간 선택 메뉴 */}
      <Menu
        anchorEl={periodFilter.anchorEl}
        open={Boolean(periodFilter.anchorEl)}
        onClose={periodFilter.handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 120,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        {periodFilter.periodOptions.map((option) => (
          <MenuItem
            key={option.value || 'all'}
            onClick={() => periodFilter.handlePeriodSelect(option.value, handlePeriodChange)}
            selected={periodFilter.selectedPeriod === option.value}
            sx={{
              fontSize: '0.875rem',
              minHeight: 36,
              '&.Mui-selected': {
                bgcolor: '#f5f5f5',
                '&:hover': {
                  bgcolor: '#eeeeee'
                }
              }
            }}
          >
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* 로딩 상태 */}
      {loading && (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Skeleton variant="rectangular" width="100%" height={120} />
                <CardContent sx={{ p: 1.5 }}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="60%" height={16} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 에러 상태 */}
      {error && (
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
          {error}
        </Typography>
      )}

      {/* 영상 그리드 */}
      {!loading && !error && videos.length > 0 && (
        <Grid container spacing={2}>
          {videos.map((video) => (
            <PopularVideoCard 
              key={video.videoId}
              video={video}
              onVideoClick={handleVideoClick}
              truncateTitle={truncateTitle}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default PopularVideos; 